<template>
  <div>
    <!-- Announcements Modal -->
    <AnnouncementsModal v-model="announcementStore.showModal"
      :announcements="announcementStore.publishedAnnouncements" />
    <v-app-bar flat color="white" height="50">
      <!-- Left: Logo -->
      <template #prepend>
        <img @click="goHome" src="@/assets/images/layout/omd-logo.png" height="35" alt="OMD Live"
          class="cursor-pointer" />
      </template>

      <!-- Right -->
      <template #append>
        <!-- GP % Dropdown -->
        <v-menu v-if="canSeeGp" v-model="gpMenuOpen" transition="fade-transition" :close-on-content-click="false">
          <template #activator="{ props }">
            <v-btn v-bind="props" class="gp-pill-btn me-2" variant="flat" color="#F1F5F9" rounded="pill"
              @click="handleGpClick">
              <div class="d-flex align-center">
                <v-icon size="14" color="#00363F" class="me-1">mdi-chart-pie</v-icon>
                <span class="gp-value">GP {{ totalGpPercentage }}</span>
              </div>
            </v-btn>
          </template>

          <v-card elevation="10" rounded="xl" min-width="280" class="gp-menu-card">
            <div class="gp-menu-header pa-4 pb-2">
              <div class="text-subtitle-1 font-weight-bold" style="color: #64748b">
                Total Growth Profit %
              </div>
              <div class="text-h5 font-weight-bold mt-1" style="color: #00363f; font-size: 14px;">
                {{ totalGpPercentage }}
              </div>
            </div>

            <v-divider class="mx-4" />

            <div class="gp-menu-body pa-2">
              <div v-if="gpLoading" class="d-flex justify-center pa-6">
                <v-progress-circular indeterminate color="#00363F" size="24" />
              </div>
              <div v-else-if="!vendorGpData.length" class="text-center pa-6" style="color: #94a3b8">
                No vendor data available
              </div>
              <div v-else class="pa-2">
                <div v-for="vendor in vendorGpData" :key="vendor.name"
                  class="vendor-item d-flex align-center justify-space-between px-3 py-2 mb-1">
                  <div class="d-flex align-center">
                    <div class="vendor-dot me-3"></div>
                    <span class="font-weight-medium" style="color: #1e293b; font-size: 12px;">{{ vendor.name }}</span>
                  </div>
                  <span class="font-weight-bold" style="color: #00363f; font-size: 14px;">{{ vendor.percentage
                  }}%</span>
                </div>
              </div>
            </div>

            <v-divider class="mx-4" />

            <div class="pa-4">
              <v-btn block variant="tonal" color="#00363F" rounded="lg" class="text-capitalize" to="/gp">
                More Details
              </v-btn>
            </div>
          </v-card>
        </v-menu>
        <!-- Calendar / Events Dropdown -->
        <v-menu v-model="calendarOpen" transition="fade-transition" :close-on-content-click="false"
          @update:model-value="onCalendarToggle">
          <template #activator="{ props }">
            <v-btn v-bind="props" icon size="x-small" variant="text" class="calendar-btn me-4">
              <v-icon size="16" color="#00363F">mdi-calendar-month</v-icon>
            </v-btn>
          </template>

          <v-card class="cal-card" elevation="10" rounded="xl">
            <!-- Calendar Header -->
            <div class="cal-header">
              <button class="cal-nav-btn" @click="prevMonth">‹</button>
              <span class="cal-month-label">{{ currentMonthLabel }}</span>
              <button class="cal-nav-btn" @click="nextMonth">›</button>
            </div>

            <v-divider />

            <!-- Loading -->
            <div v-if="eventsLoading" class="cal-loading">
              <v-progress-circular indeterminate color="#00363F" size="24" />
            </div>

            <div v-else class="cal-body">
              <!-- Weekday Headers -->
              <div class="cal-weekdays">
                <span v-for="d in weekdays" :key="d">{{ d }}</span>
              </div>

              <!-- Days Grid -->
              <div class="cal-days">
                <div v-for="day in calendarDays" :key="day.key" class="cal-day" :class="{
                  'cal-day--muted': !day.inMonth,
                  'cal-day--today': day.isToday,
                }">
                  <!-- Day Number Overlaid -->
                  <span class="cal-day-number" :class="{
                    'cal-day-number--active': day.hasEvent && day.events[0].lane === 0
                  }">
                    {{ day.date.getDate() }}
                  </span>

                  <!-- Event Bars Container -->
                  <div v-if="day.hasEvent" class="cal-day-events">
                    <div v-for="ev in day.events" :key="ev.id" class="cal-event-bar" :class="{
                      'cal-event-bar--start': ev.isStart,
                      'cal-event-bar--end': ev.isEnd,
                      'cal-event-bar--single': !ev.isMultiDay,
                    }" :style="{
                      background: ev.color,
                      top: ev.lane * 26 + 'px',
                      zIndex: 2,
                    }">
                      <!-- Tooltip -->
                      <v-tooltip activator="parent" location="top" max-width="260">
                        <div class="cal-tooltip">
                          <div class="cal-tooltip-event">
                            <div class="cal-tt-name d-flex align-center gap-2">
                              <span class="tooltip-color-indicator"
                                :style="{ background: ev.color, width: '6px', height: '6px', borderRadius: '50%' }"></span>
                              {{ ev.event_name }}
                            </div>
                            <div class="cal-tt-vendor">{{ ev.vendor_name }}</div>
                            <div class="cal-tt-cost">
                              {{ ev.assigned_users?.map(u => u.name).join(', ') || 'No Name' }}
                            </div>
                          </div>
                        </div>
                      </v-tooltip>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Bottom: events of this month summary -->
              <div v-if="currentMonthEvents.length" class="cal-events-list">
                <v-divider class="mb-2" />
                <div v-for="ev in currentMonthEvents" :key="ev.id" class="cal-event-row">
                  <div class="cal-event-dot-sm" :style="{ background: eventColorMap[ev.id] || '#00A896' }"></div>
                  <div class="cal-event-text">
                    <span class="cal-event-name">{{ ev.event_name }}</span>
                    <span class="cal-event-date">{{
                      formatEventDate(ev._day)
                    }}</span>
                  </div>
                </div>
              </div>
              <div v-else class="cal-no-events">No events this month</div>
            </div>
          </v-card>
        </v-menu>

        <!-- Stock Comparison Menu -->
        <v-menu v-model="stockMenuOpen" transition="fade-transition" :close-on-content-click="false"
          @update:model-value="onStockMenuToggle">
          <template #activator="{ props }">
            <v-btn v-bind="props" icon size="x-small" variant="text" class="calendar-btn me-2"
              @click="handleStockNotifyClick">
              <v-icon size="20" color="#00363F">mdi-package-variant-closed</v-icon>
            </v-btn>
          </template>

          <v-card elevation="10" rounded="xl" min-width="360" class="notify-menu-card">
            <div class="notify-header">
              <div class="notify-nav">
                <v-btn icon size="x-small" variant="text" :disabled="!canGoOlderNotifyDay" @click="goOlderNotifyDay">
                  <v-icon size="18" color="#00363F">mdi-chevron-left</v-icon>
                </v-btn>

                <div class="notify-title">{{ selectedNotifyDayLabel }}</div>

                <v-btn icon size="x-small" variant="text" :disabled="!canGoNewerNotifyDay" @click="goNewerNotifyDay">
                  <v-icon size="18" color="#00363F">mdi-chevron-right</v-icon>
                </v-btn>
              </div>
              <v-btn size="small" variant="text" class="notify-refresh" :loading="notificationsLoading"
                @click="fetchNotifications">
                <v-icon>mdi-refresh</v-icon>
              </v-btn>
            </div>

            <v-divider />

            <div v-if="notificationsLoading" class="notify-loading">
              <v-progress-circular indeterminate color="#00363F" size="24" />
            </div>

            <div v-else>
              <div v-if="notificationsError" class="notify-error">
                {{ notificationsError }}
              </div>

              <div v-else class="notify-body">
                <div v-if="selectedNotificationGroup" class="notify-group">
                  <div v-if="!selectedNotificationGroup.items.length" class="notify-empty">
                    No stock data yet
                  </div>
                  <div v-for="n in selectedNotificationGroup.items" :key="n.id || n.created_at || n.type || JSON.stringify(n)"
                    class="notify-card">
                    <div class="notify-vendor">{{ getNotifyVendorName(n) }}</div>
                    <div v-for="(it, idx) in getNotifyItems(n)" :key="`${n.id || n.created_at}-it-${idx}`"
                      class="notify-line">
                      <span class="notify-cat">{{ it.cat_number }}</span>
                      <span class="notify-status">
                        <img v-if="it.status === 'decreased'" src="@/assets/images/layout/down-grade.svg" alt="" />
                        <img v-else src="@/assets/images/layout/up-grade.svg" alt="" />
                      </span>
                      <span class="notify-diff">{{ it.diff }}</span>
                    </div>
                  </div>
                </div>
                <div v-else-if="!notifications.length" class="notify-empty">No data available</div>
              </div>
            </div>
          </v-card>
        </v-menu>

        <!-- Sales Comparison Menu -->
        <v-menu v-model="salesMenuOpen" transition="fade-transition" :close-on-content-click="false"
          @update:model-value="onSalesMenuToggle">
          <template #activator="{ props }">
            <v-btn v-bind="props" icon size="x-small" variant="text" class="calendar-btn me-2">
              <v-icon size="20" color="#00363F">mdi-finance</v-icon>
            </v-btn>
          </template>

          <v-card elevation="10" rounded="xl" min-width="360" class="notify-menu-card">
            <div class="notify-header">
              <div class="notify-title px-2">Sales Comparison</div>
              <div v-if="salesStore.salesCompareSummary.date_context" class="sales-date-badge">
                {{ salesStore.salesCompareSummary.date_context.current_date }} vs {{ salesStore.salesCompareSummary.date_context.previous_date }}
              </div>
              <v-btn size="small" variant="text" class="notify-refresh" :loading="loadingNotify"
                @click="handleSalesRefresh">
                <v-icon>mdi-refresh</v-icon>
              </v-btn>
            </div>

            <v-divider />

            <div class="sales-compare-view">
              <div v-if="loadingNotify" class="notify-loading py-8">
                <v-progress-circular indeterminate color="#00363F" size="24" />
              </div>

              <div v-else-if="salesStore.salesCompareSummary.current_total_value !== undefined" class="sales-results px-3 py-2">
                <!-- Global Summary Card -->
                <div class="sales-summary-card mb-3">
                  <div class="sum-row d-flex justify-space-between align-center mb-1">
                    <span class="sum-label font-weight-bold">Total Diff:</span>
                    <span class="sum-val font-weight-bold" :style="{ color: getSalesDiffColor(salesStore.salesCompareSummary.diff_total_value) }">
                      <v-icon size="14" class="me-1">{{ getSalesDiffIcon(salesStore.salesCompareSummary.diff_total_value) }}</v-icon>
                      {{ formatSAR(salesStore.salesCompareSummary.diff_total_value) }}
                    </span>
                  </div>
                  <v-divider class="my-2" />
                  <div class="d-flex justify-space-around align-center pt-1">
                    <div class="text-center">
                       <div class="text-caption text-success font-weight-bold">{{ salesStore.salesCompareSummary.increased_count }}</div>
                       <div class="text-tiny text-grey">Increased</div>
                    </div>
                    <div class="text-center">
                       <div class="text-caption text-error font-weight-bold">{{ salesStore.salesCompareSummary.decreased_count }}</div>
                       <div class="text-tiny text-grey">Decreased</div>
                    </div>
                    <div class="text-center">
                       <div class="text-caption text-grey font-weight-bold">{{ salesStore.salesCompareSummary.unchanged_count }}</div>
                       <div class="text-tiny text-grey">Unchanged</div>
                    </div>
                  </div>
                </div>

                <!-- Vendor list -->
                <div v-if="salesStore.salesCompareData.length" class="sales-items-list notify-body">
                  <div v-for="vendor in salesStore.salesCompareData" :key="vendor.vendor_id" class="sales-item-row mb-2">
                    <div class="item-name d-flex flex-column">
                      <span class="font-weight-bold">{{ vendor.vendor_name }}</span>
                      <span class="text-tiny text-grey">#{{ vendor.vendor_number }}</span>
                    </div>
                    <div class="item-diff text-end d-flex flex-column" :style="{ color: getSalesDiffColor(vendor.diff_total_value) }">
                      <span class="font-weight-bold">{{ formatSAR(vendor.diff_total_value) }}</span>
                      <span class="text-tiny">{{ vendor.status }}</span>
                    </div>
                  </div>
                </div>
                <div v-else class="notify-empty text-center pb-4">No vendor data available</div>
              </div>
              <div v-else class="notify-empty py-8 text-center">Failed to load comparison</div>
            </div>
          </v-card>
        </v-menu>

        <!-- Announcements Icon -->
        <v-btn v-if="isAuthenticated" icon size="x-small" variant="text" class="calendar-btn me-4"
          @click="handleAnnouncementsClick">
          <v-icon size="18" color="#00363F">mdi-bullhorn</v-icon>
        </v-btn>

        <!-- User Dropdown -->
        <v-menu transition="fade-transition" offset-y :close-on-content-click="false">
          <template #activator="{ props }">
            <div v-bind="props" class="user-trigger">
              <v-avatar size="35" class="me-2">
                <img width="30" height="30" :src="userImage" alt="User" />
              </v-avatar>
              <div class="text-start">
                <div class="user-name">{{ user.name }}</div>
                <div class="user-role">{{ userRole }}</div>
              </div>
              <v-icon class="ms-1" size="18" color="#9AA0A6">mdi-chevron-down</v-icon>
            </div>
          </template>

          <v-card class="menu-card" elevation="10" rounded="xl" min-width="280">
            <!-- Header -->
            <div class="menu-header">
              <v-avatar size="35" class="me-3">
                <img width="35" height="35" :src="userImage" alt="User" />
              </v-avatar>
              <div>
                <div class="header-name">{{ user.name }}</div>
                <div class="header-email">{{ user.email }}</div>
              </div>
            </div>

            <v-divider class="my-1" />

            <!-- Actions -->
            <v-list nav density="comfortable" class="menu-list">
              <v-list-item to="/profile" rounded="lg">
                <template #prepend><v-icon size="18">mdi-account-circle</v-icon></template>
                <v-list-item-title style="font-size: 14px;">Profile</v-list-item-title>
              </v-list-item>
              <v-list-item v-if="isAdmin" to="/users-management" rounded="lg">
                <template #prepend><v-icon size="18">mdi-account-group</v-icon></template>
                <v-list-item-title style="font-size: 14px;">Users List</v-list-item-title>
              </v-list-item>
              <v-list-item v-if="isAdmin" to="/roles-management" rounded="lg">
                <template #prepend><v-icon size="18">mdi-account-key</v-icon></template>
                <v-list-item-title style="font-size: 14px;">Roles List</v-list-item-title>
              </v-list-item>
            </v-list>

            <v-divider class="my-1" />

            <!-- Footer -->
            <div class="menu-footer">
              <v-btn variant="tonal" color="#00363F" class="w-100" rounded="lg" @click="logOut">
                <v-icon start size="14">mdi-logout</v-icon> Logout
              </v-btn>
            </div>
          </v-card>
        </v-menu>
      </template>
    </v-app-bar>
  </div>
</template>
<script>
import apiClient from "@/core/buildRequest";
import { useAuthStore } from "@/stores/auth";
import { mapState, mapActions } from "pinia";
import defaultAvatar from "@/assets/images/layout/Avatar.svg";
import AnnouncementsModal from "@/components/AnnouncementsModal.vue";
import { useAnnouncementStore } from "@/stores/announcements";
import { useSalesStore } from "@/stores/sales/index.js";
import { useGpStore } from "@/stores/gpStore/gpStore";
import { toast } from "vue3-toastify";

const formatDateKey = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

export default {
  name: "TopNavbar",
  components: {
    AnnouncementsModal,
  },
  data() {
    const now = new Date();
    return {
      calendarOpen: false,
      allDays: [], // raw days from API [ { day, count, events } ]
      eventsLoading: false,
      viewDate: new Date(now.getFullYear(), now.getMonth(), 1),
      weekdays: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
      selectedVendorForNotify: null,
      loadingNotify: false,
      apiFailMsg: "",
      notifications: [],
      notificationsLoading: false,
      notificationsError: "",
      notificationsPagination: null,
      selectedNotifyDayKey: formatDateKey(new Date()),
      notifyVendors: [
        { id: 1, label: "Allurion Tech" },
        { id: 4, label: "Bariatric Soult" },
        { id: 3, label: "Victor Medical" },
        { id: 2, label: "CM Endo Normal" },
      ],
      gpMenuOpen: false,
      gpLoading: false,
      gpStore: useGpStore(),
      stockMenuOpen: false,
      salesMenuOpen: false,
    };
  },
  computed: {
    currentMonthLabel() {
      return this.viewDate.toLocaleString("en-US", {
        month: "long",
        year: "numeric",
      });
    },

    // Map of dateKey -> day object from API
    eventMap() {
      const map = new Map();
      this.allDays.forEach((d) => {
        if (d.count > 0) map.set(d.day, d);
      });
      return map;
    },

    // All events flattened for current view month
    currentMonthEvents() {
      const month = this.viewDate.getMonth();
      const year = this.viewDate.getFullYear();
      const result = [];
      this.allDays.forEach((d) => {
        const date = new Date(d.day);
        if (date.getMonth() === month && date.getFullYear() === year) {
          (d.events || []).forEach((ev) => result.push({ ...ev, _day: d.day }));
        }
      });
      return result;
    },

    // Robustly derive events from allDays with calculated start/end dates
    allEventsList() {
      const eventsMap = new Map();
      this.allDays.forEach((d) => {
        (d.events || []).forEach((ev) => {
          if (!ev || !ev.id) return;
          const time = new Date(d.day).getTime();
          if (!eventsMap.has(ev.id)) {
            eventsMap.set(ev.id, {
              ...ev,
              minTime: time,
              maxTime: time,
            });
          } else {
            const item = eventsMap.get(ev.id);
            if (time < item.minTime) item.minTime = time;
            if (time > item.maxTime) item.maxTime = time;
          }
        });
      });
      return Array.from(eventsMap.values()).map((ev) => ({
        ...ev,
        from_date: new Date(ev.minTime).toISOString().split("T")[0],
        to_date: new Date(ev.maxTime).toISOString().split("T")[0],
      }));
    },

    // Palette of modern, premium colors
    eventColorMap() {
      const colors = [
        "#00A896", // Default Teal
        "#FF6B6B", // Coral
        "#4D96FF", // Sky Blue
        "#FFD93D", // Mustard
        "#6BCB77", // Green
        "#9747FF", // Purple
        "#FF9F43", // Orange
        "#2C3E50", // Dark Slate
      ];
      const colorMap = {};
      this.allEventsList.forEach((event, idx) => {
        colorMap[event.id] = colors[idx % colors.length];
      });
      return colorMap;
    },

    calendarDays() {
      const year = this.viewDate.getFullYear();
      const month = this.viewDate.getMonth();
      const today = formatDateKey(new Date());

      const firstDay = new Date(year, month, 1);
      const startOffset = firstDay.getDay();
      const startDate = new Date(year, month, 1 - startOffset);

      // Find events in the current 42-day view for lane calculation
      const viewStart = new Date(startDate);
      const viewEnd = new Date(startDate);
      viewEnd.setDate(viewEnd.getDate() + 41);
      viewEnd.setHours(23, 59, 59, 999);

      const eventsInView = this.allEventsList.filter((e) => {
        const from = new Date(e.from_date);
        const to = new Date(e.to_date);
        return from <= viewEnd && to >= viewStart;
      }).sort((a, b) => new Date(a.from_date) - new Date(b.from_date));

      // Consistent lanes across the 42-day view
      const lanes = [];
      const eventToLane = {};
      eventsInView.forEach((event) => {
        let laneIndex = lanes.findIndex((laneEvents) => {
          return !laneEvents.some((e) => this.overlaps(e, event));
        });
        if (laneIndex === -1) {
          laneIndex = lanes.length;
          lanes.push([event]);
        } else {
          laneIndex = lanes.length - 1;
          lanes[laneIndex].push(event);
        }
        eventToLane[event.id] = laneIndex;
      });

      const days = [];
      for (let i = 0; i < 42; i++) {
        const current = new Date(
          startDate.getFullYear(),
          startDate.getMonth(),
          startDate.getDate() + i,
        );
        const currentMs = current.getTime();
        const key = formatDateKey(current);

        const currentDayStart = new Date(currentMs).setHours(0, 0, 0, 0);
        const currentDayEnd = new Date(currentMs).setHours(23, 59, 59, 999);

        const dayEvents = eventsInView.filter((e) => {
          const from = new Date(e.from_date).setHours(0, 0, 0, 0);
          const to = new Date(e.to_date).setHours(23, 59, 59, 999);
          return currentDayEnd >= from && currentDayStart <= to;
        }).map((e) => {
          const from = new Date(e.from_date).setHours(0, 0, 0, 0);
          const to = new Date(e.to_date).setHours(23, 59, 59, 999);

          const isStart = currentDayStart <= from && currentDayEnd >= from;
          const isEnd = currentDayStart <= to && currentDayEnd >= to;
          const isMultiDay = (to - from) >= 86400000;

          return {
            ...e,
            color: this.eventColorMap[e.id] || "#00A896",
            lane: eventToLane[e.id],
            isStart,
            isEnd,
            isMultiDay,
          };
        }).sort((a, b) => a.lane - b.lane);

        days.push({
          key,
          date: current,
          inMonth: current.getMonth() === month,
          isToday: key === today,
          hasEvent: dayEvents.length > 0,
          events: dayEvents,
        });
      }
      return days;
    },
    ...mapState(useAuthStore, [
      "user",
      "userRole",
      "isAdmin",
      "isSuperAdmin",
      "isAuthenticated",
      "canSeeGp",
    ]),
    announcementStore() {
      return useAnnouncementStore();
    },
    publishedAnnouncements() {
      return this.announcementStore.publishedAnnouncements;
    },
    userImage() {
      return this.user?.image || defaultAvatar;
    },
    salesStore() {
      return useSalesStore();
    },
    selectedNotificationGroup() {
      const key = this.selectedNotifyDayKey;
      const list = Array.isArray(this.notifications) ? this.notifications : [];
      const items = list.filter((n) => this.getNotifyDayKey(n) === key);
      return {
        day: this.formatNotifyDayKey(key),
        sortKey: key,
        items,
      };
    },
    selectedNotifyDayLabel() {
      return this.formatNotifyDayKey(this.selectedNotifyDayKey);
    },
    canGoOlderNotifyDay() {
      return true;
    },
    canGoNewerNotifyDay() {
      const todayKey = formatDateKey(new Date());
      return (
        String(this.selectedNotifyDayKey).localeCompare(String(todayKey)) < 0
      );
    },
    totalGpPercentage() {
      const card = this.gpStore.gpCardsTotal.find(
        (c) => c.key === "gp_percentage",
      );
      return card ? card.value : "0";
    },
    vendorGpData() {
      if (!this.gpStore.gpData?.charts?.gp_per_supplier) return [];
      return this.gpStore.gpData.charts.gp_per_supplier.map((s) => {
        const rev = parseFloat(s.revenue) || 0;
        const cost = parseFloat(s.cost) || 0;
        const gp = rev > 0 ? ((rev - cost) / rev) * 100 : 0;

        let shortName = s.vendor_name || "";
        const n = shortName.toUpperCase();
        if (n.includes("ALLURION")) shortName = "ALLURION";
        else if (n.includes("ENDO") || n.includes("COOK"))
          shortName = "COOK ENDO";
        else if (n.includes("VICTOR")) shortName = "VICTOR";
        else if (n.includes("BARIATRIC") || n.includes("BSI"))
          shortName = "BSI";

        return {
          name: shortName,
          percentage: gp.toFixed(1),
        };
      });
    },
  },
  mounted() {
    this.fetchPublishedAnnouncements();
    this.gpStore.fetchGpData();
  },
  methods: {
    goHome() {
      this.$router.push("/home");
    },
    parseNotifyDate(dateStr) {
      if (typeof dateStr !== "string" || !dateStr) return null;

      const isoLike = dateStr.includes(" ")
        ? dateStr.replace(" ", "T")
        : dateStr;
      const d = new Date(isoLike);
      return !isNaN(d.getTime()) ? d : null;
    },
    overlaps(a, b) {
      const aStart = new Date(a.from_date).setHours(0, 0, 0, 0);
      const aEnd = new Date(a.to_date).setHours(23, 59, 59, 999);
      const bStart = new Date(b.from_date).setHours(0, 0, 0, 0);
      const bEnd = new Date(b.to_date).setHours(23, 59, 59, 999);
      return aStart <= bEnd && bStart <= aEnd;
    },
    async onCalendarToggle(isOpen) {
      if (isOpen && !this.allDays.length) {
        await this.fetchEvents();
      }
    },
    async fetchEvents() {
      this.eventsLoading = true;
      try {
        const token = localStorage.getItem("user_token");
        const res = await fetch("https://backend.omdlive.com/api/events", {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
          },
        });
        const json = await res.json();
        this.allDays = json?.data?.calendar?.days || [];
      } catch (e) {
        console.error("Failed to fetch events:", e);
        this.allDays = [];
      } finally {
        this.eventsLoading = false;
      }
    },
    prevMonth() {
      this.viewDate = new Date(
        this.viewDate.getFullYear(),
        this.viewDate.getMonth() - 1,
        1,
      );
    },
    nextMonth() {
      this.viewDate = new Date(
        this.viewDate.getFullYear(),
        this.viewDate.getMonth() + 1,
        1,
      );
    },
    formatSAR(val) {
      if (val == null) return "0 SAR";
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "SAR",
        minimumFractionDigits: 0,
      }).format(val);
    },
    formatEventDate(dateStr) {
      if (!dateStr) return "";
      return new Date(dateStr).toLocaleDateString("en-US", {
        day: "numeric",
        month: "short",
      });
    },
    async logOut() {
      try {
        await apiClient.delete("sign-out");
      } catch (error) {
        // handled by interceptor
      } finally {
        this.clearAuth();
        this.$router.push({ path: "/login", query: { logout: 1 } });
      }
    },
    ...mapActions(useAuthStore, ["clearAuth"]),
    async fetchPublishedAnnouncements() {
      await this.announcementStore.fetchPublishedAnnouncements();
    },
    handleAnnouncementsClick() {
      if (this.isSuperAdmin) {
        this.$router.push("/announcements");
      } else {
        if (this.publishedAnnouncements.length > 0) {
          this.announcementStore.openModal();
        } else {
          toast.info("No active announcements");
        }
      }
    },
    onStockMenuToggle(isOpen) {
      if (isOpen) {
        this.selectedNotifyDayKey = formatDateKey(new Date());
        if (!this.notifications.length) this.fetchNotifications();
      } else {
        this.resetNotifyMenu();
      }
    },
    onSalesMenuToggle(isOpen) {
      if (isOpen) {
        this.handleSalesRefresh();
      } else {
        this.resetNotifyMenu();
      }
    },
    async fetchNotifications() {
      this.notificationsLoading = true;
      this.notificationsError = "";
      try {
        const res = await apiClient.get("notifications", { showLoader: false });
        const body = res?.data ?? {};
        const data = body?.data ?? body;
        this.notificationsPagination = data?.notifications?.pagination ?? null;
        const list = Array.isArray(data?.notifications?.data)
          ? data.notifications.data
          : Array.isArray(data?.notifications)
            ? data.notifications
            : Array.isArray(data)
              ? data
              : [];
        this.notifications = list;
      } catch (e) {
        this.notifications = [];
        this.notificationsError = "Failed to load notifications";
      } finally {
        this.notificationsLoading = false;
      }
    },
    getNotifyVendorName(n) {
      const raw = n?.data?.vendor_name ?? n?.data?.vendor?.name;
      return raw || n?.vendor_name || "";
    },
    getNotifyItems(n) {
      const items = n?.data?.items;
      return Array.isArray(items) ? items : [];
    },
    getNotifyDayKey(n) {
      const dateField = n?.date;
      const parsedFromDate = this.parseNotifyDate(dateField);
      if (parsedFromDate) {
        const y = parsedFromDate.getFullYear();
        const m = String(parsedFromDate.getMonth() + 1).padStart(2, "0");
        const day = String(parsedFromDate.getDate()).padStart(2, "0");
        return `${y}-${m}-${day}`;
      }

      const createdAt = n?.created_at;
      if (
        typeof createdAt === "string" &&
        (createdAt.includes("منذ") || createdAt.includes("قبل"))
      ) {
        const d = new Date(); // Current time browser side

        // Match numbers if any
        const numMatch = createdAt.match(/\d+/);
        const amount = numMatch ? parseInt(numMatch[0]) : 1;

        if (createdAt.includes("دقيق") || createdAt.includes("ثان")) {
          // just minutes/seconds - keep today
        } else if (createdAt.includes("ساع")) {
          d.setHours(d.getHours() - amount);
        } else if (createdAt.includes("يوم") || createdAt.includes("أيام")) {
          d.setDate(d.getDate() - amount);
        } else if (createdAt.includes("شهر")) {
          d.setMonth(d.getMonth() - amount);
        } else if (createdAt.includes("سنة")) {
          d.setFullYear(d.getFullYear() - amount);
        }

        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, "0");
        const day = String(d.getDate()).padStart(2, "0");
        return `${y}-${m}-${day}`;
      }
      const parsedFromCreatedAt = createdAt
        ? this.parseNotifyDate(createdAt)
        : null;
      if (parsedFromCreatedAt) {
        const y = parsedFromCreatedAt.getFullYear();
        const m = String(parsedFromCreatedAt.getMonth() + 1).padStart(2, "0");
        const day = String(parsedFromCreatedAt.getDate()).padStart(2, "0");
        return `${y}-${m}-${day}`;
      }
      const now = new Date();
      const y = now.getFullYear();
      const m = String(now.getMonth() + 1).padStart(2, "0");
      const day = String(now.getDate()).padStart(2, "0");
      return `${y}-${m}-${day}`;
    },
    formatNotifyDayKey(key) {
      const d = key ? new Date(`${key}T00:00:00`) : null;
      if (d && !isNaN(d.getTime())) {
        return d.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
        });
      }
      return key || "Notifications";
    },
    goOlderNotifyDay() {
      const d = new Date(`${this.selectedNotifyDayKey}T00:00:00`);
      if (!isNaN(d.getTime())) {
        d.setDate(d.getDate() - 1);
        this.selectedNotifyDayKey = formatDateKey(d);
      }
    },
    goNewerNotifyDay() {
      if (!this.canGoNewerNotifyDay) return;
      const d = new Date(`${this.selectedNotifyDayKey}T00:00:00`);
      if (!isNaN(d.getTime())) {
        d.setDate(d.getDate() + 1);
        this.selectedNotifyDayKey = formatDateKey(d);
      }
    },
    async handleStockNotifyClick() {
      this.loadingNotify = true;
      this.apiFailMsg = "";
      try {
        await this.salesStore.getStockCompareNotifyData();
        await this.fetchNotifications();
      } catch (e) {
        await this.fetchNotifications();
      } finally {
        this.loadingNotify = false;
      }
    },
    async handleSalesRefresh() {
      this.loadingNotify = true;
      this.apiFailMsg = "";
      try {
        await this.salesStore.getSalesCompareData();
      } catch (e) {
        this.apiFailMsg = "Error fetching data.";
        this.notificationsError = this.apiFailMsg;
      } finally {
        this.loadingNotify = false;
      }
    },
    async handleSalesVendorClick(vendor) {
      // Deprecated in favor of global today view, but kept for future use
      if (!vendor) return;
      this.selectedVendorForNotify = vendor;
      this.handleSalesRefresh();
    },
    getSalesDiffColor(diff) {
      if (!diff) return "#94a3b8";
      return diff > 0 ? "#10b981" : "#ef4444";
    },
    getSalesDiffIcon(diff) {
      if (!diff) return "";
      return diff > 0 ? "mdi-arrow-up" : "mdi-arrow-down";
    },
    resetNotifyMenu() {
      this.apiFailMsg = "";
      this.notificationsError = "";
    },
    async handleGpClick() {
      if (!this.gpStore.gpData) {
        this.gpLoading = true;
        try {
          await this.gpStore.fetchGpData();
        } catch (e) {
          console.error("Failed to fetch GP data:", e);
        } finally {
          this.gpLoading = false;
        }
      }
    },
  },
};
</script>

<style scoped>
:deep(.v-toolbar__content) {
  box-shadow: 0px 4px 40px 0px #00000014;
  padding: 0 48px !important;
}

.gp-pill-btn {
  text-transform: none !important;
  font-weight: 600 !important;
  color: #00363f !important;
  height: 38px !important;
  padding: 0 16px !important;
}

.gp-value {
  font-size: 12px;
}

.gp-menu-header .text-subtitle-1 {
  font-size: 14px !important;
}

.gp-menu-header .text-h5 {
  font-size: 16px !important;
}

.vendor-item {
  background: #f8fafc;
  border-radius: 10px;
  transition: background 0.2s;
}

.vendor-item:hover {
  background: #f1f5f9;
}

.vendor-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background-color: #00363f;
}

.cursor-pointer {
  cursor: pointer;
}

.notify-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
}

.notify-nav {
  display: flex;
  align-items: center;
  gap: 6px;
}

.notify-title {
  font-weight: 600;
  color: #0f172a;
  font-size: 14px;
}

.notify-loading {
  padding: 20px 16px;
  display: flex;
  justify-content: center;
}

.notify-error {
  padding: 14px 16px;
  color: #b91c1c;
  font-size: 13px;
}

.notify-empty {
  padding: 14px 16px;
  color: #596a6d;
  font-size: 13px;
}

.notify-body {
  max-height: 420px;
  overflow: auto;
}

.notify-group {
  padding: 10px 12px;
}

.compare-toggle-container {
  padding: 0 16px 12px 16px;
  display: flex;
  justify-content: center;
}

.compare-toggle {
  background: #f1f5f9;
  border-radius: 12px !important;
  height: 32px !important;
  padding: 4px;
}

.compare-toggle :deep(.v-btn) {
  min-width: 80px !important;
  height: 24px !important;
  font-size: 11px !important;
  border-radius: 8px !important;
}

.vendor-selector-row {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  border-bottom: 1px solid #f1f5f9;
  padding-bottom: 12px;
}

.vendor-pill {
  padding: 4px 10px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 20px;
  font-size: 11px;
  color: #64748b;
  cursor: pointer;
  transition: all 0.2s;
}

.vendor-pill:hover {
  background: #f1f5f9;
}

.vendor-pill--active {
  background: #00363f;
  color: white;
  border-color: #00363f;
}

.sales-summary-card {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 12px;
}

.sales-date-badge {
  font-size: 10px;
  background: #00363f10;
  color: #00363f;
  padding: 2px 8px;
  border-radius: 20px;
  max-width: 160px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.text-tiny {
  font-size: 10px;
}

.sum-label {
  font-size: 12px;
  color: #64748b;
}

.sum-val {
  font-size: 13px;
  color: #0f172a;
  font-weight: 600;
}

.sales-item-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 10px;
  background: #ffffff;
  border: 1px solid #f1f5f9;
  border-radius: 8px;
}

.item-name {
  font-size: 12px;
  color: #0f172a;
}

.item-diff {
  font-size: 12px;
  font-weight: 700;
}

.notify-day {
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
  margin: 6px 0 10px;
}

.notify-card {
  border: 1px solid #eef1f6;
  border-radius: 12px;
  padding: 10px 12px;
  margin-bottom: 10px;
  background: #ffffff;
}

.notify-vendor {
  font-size: 13px;
  font-weight: 600;
  color: #00363f;
  margin-bottom: 6px;
}

.notify-line {
  display: flex;
  gap: 10px;
  font-size: 12px;
  color: #0f172a;
  padding: 4px 0;
}

.notify-cat {
  font-weight: 600;
}

.notify-status {
  color: #64748b;
  text-transform: capitalize;
}

.notify-diff {
  font-variant-numeric: tabular-nums;
}

.notify-item-title {
  font-size: 13px;
  font-weight: 600;
}

.notify-item-subtitle {
  font-size: 12px;
  color: #596a6d;
}

.notify-card-title {
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 2px;
}

.notify-card-body {
  font-size: 12px;
  color: #64748b;
  margin-bottom: 8px;
  line-height: 1.4;
}

/* ===== User Dropdown ===== */
.user-trigger {
  display: flex;
  align-items: center;
  cursor: pointer;
  padding: 6px 10px;
  border-radius: 12px;
  transition: background-color 0.2s ease;
}

.user-trigger:hover {
  background: #f5f7f8;
}

.user-name {
  font-weight: 600;
  line-height: 1;
  color: #333333;
  font-size: 12px;
}

.user-role {
  font-size: 10px;
  color: #596a6d;
  margin-top: 2px;
  font-weight: 400;
}

.menu-card {
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08) !important;
  border: 1px solid #eef0f2;
}

.menu-header {
  display: flex;
  align-items: center;
  padding: 14px 16px;
  background: linear-gradient(180deg, #ffffff 0%, #f9fbfc 100%);
}

.header-name {
  font-weight: 700;
  font-size: 14px;
}

.header-email {
  font-size: 12px;
  color: #596a6d;
}

.menu-list :deep(.v-list-item) {
  padding: 10px 14px;
  margin: 2px 8px;
  border-radius: 12px;
}

.menu-list :deep(.v-list-item:hover) {
  background: #f3f6f8;
}

.menu-list :deep(.v-icon) {
  color: #00363f;
}

.menu-footer {
  padding: 12px;
}

.w-100 {
  width: 100%;
}

/* ===== Calendar Dropdown ===== */
.calendar-btn {
  border: 1px solid #e2e8f0 !important;
  border-radius: 10px !important;
}

.cal-card,
.gp-menu-card,
.menu-card,
.notify-menu-card {
  zoom: 0.85;
  transform-origin: top right;
}

.cal-card {
  overflow: hidden;
  border: 1px solid #eef0f2;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1) !important;
  width: 320px;
}

.cal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: linear-gradient(180deg, #ffffff 0%, #f9fbfc 100%);
}

.cal-month-label {
  font-weight: 700;
  font-size: 14px;
  color: #00363f;
}

.cal-nav-btn {
  width: 28px;
  height: 28px;
  border: 1px solid #e2e8f0;
  background: white;
  border-radius: 8px;
  cursor: pointer;
  font-size: 18px;
  color: #00363f;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s;
}

.cal-nav-btn:hover {
  background: #f0fafa;
}

.cal-loading {
  display: flex;
  justify-content: center;
  padding: 32px;
}

.cal-body {
  padding: 12px;
}

.cal-weekdays {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  margin-bottom: 6px;
}

.cal-weekdays span {
  text-align: center;
  font-size: 11px;
  color: #94a3b8;
  font-weight: 500;
}

.cal-days {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  row-gap: 4px;
  column-gap: 0;
}

.cal-day {
  border: none;
  background: transparent;
  padding: 0;
  outline: none;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  position: relative;
  min-height: 48px;
}

.cal-day-number {
  width: 100%;
  height: 26px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  color: #0f172a;
  z-index: 5;
  font-weight: 500;
  position: absolute;
  top: 0;
  pointer-events: none;
}

.cal-day-number--active {
  color: #ffffff !important;
  font-weight: 700;
}

.cal-day--muted .cal-day-number {
  color: #cbd5e1;
}

.cal-day--today .cal-day-number {
  border: 1px solid #00363f;
  border-radius: 4px;
}

.cal-day-events {
  width: 100%;
  position: relative;
  margin-top: 0;
}

.cal-event-bar {
  position: absolute;
  left: 0;
  right: 0;
  height: 26px;
  cursor: pointer;
  transition: opacity 0.2s;
  border-radius: 0;
}

.cal-event-bar:hover {
  opacity: 0.8;
}

.cal-event-bar--start {
  border-top-left-radius: 4px;
  border-bottom-left-radius: 4px;
  left: 2px;
}

.cal-event-bar--end {
  border-top-right-radius: 4px;
  border-bottom-right-radius: 4px;
  right: 2px;
}

.cal-event-bar--single {
  width: 24px !important;
  height: 24px !important;
  border-radius: 50% !important;
  margin: 0 auto;
  left: 0;
  right: 0;
  top: 0 !important;
}

/* Tooltip Styles (matching EventsCalendar.vue) */
.cal-tooltip {
  padding: 3px 4px;
}

.cal-tt-name {
  font-weight: 700;
  font-size: 10px;
  color: #ffffff;
  margin-bottom: 1px;
}

.tooltip-color-indicator {
  width: 5px;
  height: 5px;
  border-radius: 50%;
  flex-shrink: 0;
  margin-right: 4px;
}

.cal-tt-vendor {
  font-size: 9px;
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: 2px;
}

.cal-tt-cost {
  font-weight: 600;
  color: #5ee9b5;
  font-size: 9px;
}

/* Events summary list */
.cal-events-list {
  margin-top: 8px;
  max-height: 140px;
  overflow-y: auto;
}

.cal-events-list::-webkit-scrollbar {
  width: 3px;
}

.cal-events-list::-webkit-scrollbar-thumb {
  background: #cad5e2;
  border-radius: 10px;
}

.cal-event-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 5px 4px;
  border-radius: 6px;
  transition: background 0.12s;
}

.cal-event-row:hover {
  background: #f5fafa;
}

.cal-event-dot-sm {
  width: 6px;
  height: 6px;
  min-width: 6px;
  border-radius: 50%;
  background: #00a896;
}

.cal-event-text {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex: 1;
  gap: 8px;
  overflow: hidden;
}

.cal-event-name {
  font-size: 12px;
  font-weight: 600;
  color: #1e293b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.cal-event-date {
  font-size: 11px;
  color: #708083;
  white-space: nowrap;
  flex-shrink: 0;
}

.cal-no-events {
  text-align: center;
  font-size: 12px;
  color: #94a3b8;
  padding: 12px 0 4px;
}

/* Tooltip */
.cal-tooltip {
  padding: 3px;
}

.cal-tt-name {
  font-weight: 700;
  font-size: 10px;
  color: #fff;
  margin-bottom: 1px;
}

.cal-tt-vendor {
  font-size: 9px;
  color: rgba(255, 255, 255, 0.75);
  margin-bottom: 2px;
}

.cal-tt-cost {
  font-size: 9px;
  font-weight: 600;
  color: #5ee9b5;
}
</style>
